import { displayNumberWithComma } from '~/plugins/functions/display';

export interface IModelQuotaResponse {
  id: number;
  name: string;
  perDay: string;
  perMonth: string;
  conversationCount: string;
  currentUsage: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export class ModelQuota {
  id: number;
  name: string;
  perDay: string;
  perMonth: string;
  conversationCount: string;
  currentUsage: string;
  createdAt: string | Date;
  updatedAt: string | Date;

  constructor(props = {} as IModelQuotaResponse) {
    props = props ?? {};
    this.id = props.id;
    this.name = props.name;
    this.perDay = displayNumberWithComma(props.perDay);
    this.perMonth = displayNumberWithComma(props.perMonth);
    this.conversationCount = displayNumberWithComma(props.conversationCount);
    this.currentUsage = displayNumberWithComma(props.currentUsage);
    this.createdAt = props.createdAt;
    this.updatedAt = props.updatedAt;
  }
}

export interface IModelCreateQuotaRequest {
  name: string;
  quota: string;
  hostName: string;
  urlPath: string;
  type: string;
  status: string;
}

export interface IModelQuotaDetailResponse {
  id: number;
  presetId: string;
  userId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimits: IModelQuotaLimitResponse[];
  name: string;
  presetVersionKey: string | null;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  user: IModelUserResponse;
}

export class ModelQuotaDetail {
  id: number;
  presetId: string;
  userId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimits: IModelQuotaLimitResponse[];
  name: string;
  presetVersionKey: string | null;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  user: IModelUserResponse;

  constructor(props = {} as IModelQuotaDetailResponse) {
    props = props ?? {};
    this.id = props.id;
    this.presetId = props.presetId;
    this.userId = props.userId;
    this.createdAt = props.createdAt;
    this.updatedAt = props.updatedAt;
    this.quotaLimits = (props.quotaLimits || []).map(
      (e) => new ModelQuotaLimit(e)
    );
    this.name = props.name;
    this.presetVersionKey = props.presetVersionKey;
    this.type = props.type;
    this.quotaDepartmentId = props.quotaDepartmentId;
    this.parentId = props.parentId;
    this.user = new ModelUser(props.user || {});
  }
}

export interface IModelQuotaLimitResponse {
  id: string;
  quotaId: string;
  model: string;
  type: string | Date;
  quota: any;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export class ModelQuotaLimit {
  id: string;
  quotaId: string;
  model: string;
  type: string | Date;
  quota: any;
  createdAt: string | Date;
  updatedAt: string | Date;

  constructor(props = {} as IModelQuotaLimitResponse) {
    props = props ?? ({} as IModelQuotaLimitResponse);
    this.id = props.id || '';
    this.quotaId = props.quotaId || '';
    this.model = props.model || '';
    this.type = props.type || '';
    this.quota = props.quota || {};
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
  }
}

export interface IModelQuotaUsageResponse {
  id: number;
  quotaLimitId: string;
  type: string;
  date: string;
  used: string;
  createdAt: string | Date;
  updatedAt: string | Date;
}

export class ModelQuotaUsage {
  id: number;
  quotaLimitId: string;
  type: string;
  date: string;
  used: string;
  createdAt: string | Date;
  updatedAt: string | Date;

  constructor(props = {} as IModelQuotaUsageResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.quotaLimitId = props.quotaLimitId || '';
    this.type = props.type || '';
    this.date = props.date || '';
    this.used = props.used || '';
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
  }
}

export interface IModelQuotaLimitWithUsagesResponse {
  id: number;
  quotaId: string;
  model: string;
  type: string;
  quota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaUsageList: IModelQuotaUsageResponse[];
}

export class ModelQuotaLimitWithUsages {
  id: number;
  quotaId: string;
  model: string;
  type: string;
  quota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaUsageList: ModelQuotaUsage[];

  constructor(props = {} as IModelQuotaLimitWithUsagesResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.quotaId = props.quotaId || '';
    this.model = props.model || '';
    this.type = props.type || '';
    this.quota = props.quota || '';
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.quotaUsageList = (props.quotaUsageList || []).map(
      (e) => new ModelQuotaUsage(e)
    );
  }
}

export interface IModelQuotaListResponse {
  id: number;
  department: string;
  code: string;
  overallQuota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  createdBy: string;
  updatedBy: string;
  quotaDepartmentUsages: any[];
  currentUsage: string;
}

export class ModelQuotaList {
  id: number;
  department: string;
  code: string;
  overallQuota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  createdBy: string;
  updatedBy: string;
  quotaDepartmentUsages: any[];
  currentUsage: string;

  constructor(props = {} as IModelQuotaListResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.department = props.department || '';
    this.code = props.code || '';
    this.overallQuota = displayNumberWithComma(props.overallQuota);
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.createdBy = props.createdBy || '';
    this.updatedBy = props.updatedBy || '';
    this.quotaDepartmentUsages = props.quotaDepartmentUsages || [];
    this.currentUsage = displayNumberWithComma(props.currentUsage || 0) ;
  }
}

export interface IModelQuotaOverallDetailResponse {
  id: number;
  department: string;
  code: string;
  overallQuota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  createdBy: string;
  updatedBy: string;
}

export class ModelQuotaOverallDetail {
  id: number;
  department: string;
  code: string;
  overallQuota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  createdBy: string;
  updatedBy: string;

  constructor(props = {} as IModelQuotaOverallDetailResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.department = props.department || '';
    this.code = props.code || '';
    this.overallQuota = displayNumberWithComma(props.overallQuota || 0);
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.createdBy = props.createdBy || '';
    this.updatedBy = props.updatedBy || '';
  }
}

export interface IModelQuotaDepartmentUsageResponse {
  id: number;
  quotaId: string;
  model: string;
  type: string;
  quota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaUsages: any[];
}

export class ModelQuotaDepartmentUsage {
  id: number;
  quotaId: string;
  model: string;
  type: string;
  quota: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaUsages: any[];

  constructor(props = {} as IModelQuotaDepartmentUsageResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.quotaId = props.quotaId || '';
    this.model = props.model || '';
    this.type = props.type || '';
    this.quota = displayNumberWithComma(props.quota);
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.quotaUsages = props.quotaUsages || [];
  }
}

export interface IModelUserResponse {
  id: number;
  username: string;
  email: string;
  conversations: any[];
}

export class ModelUser {
  id: number;
  username: string;
  email: string;
  conversations: any[];

  constructor(props = {} as IModelUserResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.username = props.username || '';
    this.email = props.email || '';
    this.conversations = props.conversations || [];
  }
}

export interface IModelUserQuotaResponse {
  id: number;
  presetId: string | null;
  userId: string;
  presetVersionKey: string | null;
  name: string | null;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  user: IModelUserResponse;
  currentUsage: number;
  perDay: number;
  perMonth: number;
  conversationCount: number;
}

export class ModelUserQuota {
  id: number;
  presetId: string | null;
  userId: string;
  presetVersionKey: string | null;
  name: string | null;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  user: ModelUser;
  currentUsage: number;
  perDay: number;
  perMonth: number;
  conversationCount: number;

  constructor(props = {} as IModelUserQuotaResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.presetId = props.presetId || null;
    this.userId = props.userId || '';
    this.presetVersionKey = props.presetVersionKey || null;
    this.name = props.name || null;
    this.type = props.type || '';
    this.quotaDepartmentId = props.quotaDepartmentId || null;
    this.parentId = props.parentId || '';
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.user = new ModelUser(props.user || {});
    this.currentUsage = props.currentUsage || 0;
    this.perDay = props.perDay || 0;
    this.perMonth = props.perMonth || 0;
    this.conversationCount = props.conversationCount || 0;
  }
}

export interface IModelFunctionCodeQuotaResponse {
  id: number;
  presetId: string | null;
  userId: string | null;
  presetVersionKey: string | null;
  name: string;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimits: IModelQuotaDepartmentUsageResponse[];
  userQuotas: IModelUserQuotaResponse[];
  currentUsage: number;
  perDay: number;
  perMonth: number;
  conversationCount: number;
}

export class ModelFunctionCodeQuota {
  id: number;
  presetId: string | null;
  userId: string | null;
  presetVersionKey: string | null;
  name: string;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimits: ModelQuotaDepartmentUsage[];
  userQuotas: ModelUserQuota[];
  currentUsage: number;
  perDay: number;
  perMonth: number;
  conversationCount: number;

  constructor(props = {} as IModelFunctionCodeQuotaResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.presetId = props.presetId || null;
    this.userId = props.userId || null;
    this.presetVersionKey = props.presetVersionKey || null;
    this.name = props.name || '';
    this.type = props.type || '';
    this.quotaDepartmentId = props.quotaDepartmentId || null;
    this.parentId = props.parentId || '';
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.quotaLimits = (props.quotaLimits || []).map(
      (e) => new ModelQuotaDepartmentUsage(e)
    );
    this.userQuotas = (props.userQuotas || []).map(
      (e) => new ModelUserQuota(e)
    );
    this.currentUsage = props.currentUsage || 0;
    this.perDay = props.perDay || 0;
    this.perMonth = props.perMonth || 0;
    this.conversationCount = props.conversationCount || 0;
  }
}

export interface IModelQuotaDepartmentResponse {
  id: number;
  presetId: string | null;
  userId: string | null;
  presetVersionKey: string | null;
  name: string;
  type: string;
  quotaDepartmentId: string;
  parentId: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimits: IModelQuotaDepartmentUsageResponse[];
  functionCodeQuotas: IModelFunctionCodeQuotaResponse[];
  currentUsage: number;
  perDay: number;
  perMonth: number;
  conversationCount: number;
}

export class ModelQuotaDepartment {
  id: number;
  presetId: string | null;
  userId: string | null;
  presetVersionKey: string | null;
  name: string;
  type: string;
  quotaDepartmentId: string;
  parentId: string | null;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimits: ModelQuotaDepartmentUsage[];
  functionCodeQuotas: ModelFunctionCodeQuota[];
  currentUsage: string;
  perDay: string;
  perMonth: string;
  conversationCount: string;


  constructor(props = {} as IModelQuotaDepartmentResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.presetId = props.presetId || null;
    this.userId = props.userId || null;
    this.presetVersionKey = props.presetVersionKey || null;
    this.name = props.name || '';
    this.type = props.type || '';
    this.quotaDepartmentId = props.quotaDepartmentId || '';
    this.parentId = props.parentId || null;
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.quotaLimits = (props.quotaLimits || []).map(
      (e) => new ModelQuotaDepartmentUsage(e)
    );
    this.functionCodeQuotas = (props.functionCodeQuotas || []).map(
      (e) => new ModelFunctionCodeQuota(e)
    );
    this.currentUsage = displayNumberWithComma(props.currentUsage || 0);
    this.perDay = displayNumberWithComma(props.perDay || 0);
    this.perMonth = displayNumberWithComma(props.perMonth || 0);
    this.conversationCount = displayNumberWithComma(props.conversationCount || 0) ;

  }
}

export interface IModelUserQuotaBySuperAdminResponse {
  id: number;
  presetId: string | null;
  userId: string;
  presetVersionKey: string | null;
  name: string | null;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimitList: IModelQuotaLimitWithUsagesResponse[];
  user: IModelUserResponse;
  currentUsage: number;
  perDay: number;
  perMonth: number;
  conversationCount: number;
}

export class ModelUserQuotaBySuperAdmin {
  id: number;
  presetId: string | null;
  userId: string;
  presetVersionKey: string | null;
  name: string | null;
  type: string;
  quotaDepartmentId: string | null;
  parentId: string;
  createdAt: string | Date;
  updatedAt: string | Date;
  quotaLimitList: ModelQuotaLimitWithUsages[];
  user: ModelUser;
  currentUsage: number;
  perDay: number;
  perMonth: number;
  conversationCount: number;

  constructor(props = {} as IModelUserQuotaBySuperAdminResponse) {
    props = props ?? {};
    this.id = props.id || 0;
    this.presetId = props.presetId || null;
    this.userId = props.userId || '';
    this.presetVersionKey = props.presetVersionKey || null;
    this.name = props.name || null;
    this.type = props.type || '';
    this.quotaDepartmentId = props.quotaDepartmentId || null;
    this.parentId = props.parentId || '';
    this.createdAt = props.createdAt || '';
    this.updatedAt = props.updatedAt || '';
    this.quotaLimitList = (props.quotaLimitList || []).map(
      (e) => new ModelQuotaLimitWithUsages(e)
    );
    this.user = new ModelUser(props.user || {});
    this.currentUsage = props.currentUsage || 0;
    this.perDay = props.perDay || 0;
    this.perMonth = props.perMonth || 0;
    this.conversationCount = props.conversationCount || 0;
  }
}
